import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";

import Landing from "@/pages/public/Landing";
import AllNews from "@/pages/public/AllNews";
import MemberApply from "@/pages/public/MemberApply";
import SponsorProposal from "@/pages/public/SponsorProposal";
import FollowSignup from "@/pages/public/FollowSignup";
import MemberLogin from "@/pages/public/MemberLogin";
import SetPassword from "@/pages/public/SetPassword";
import AdminAuth from "@/pages/public/AdminAuth";

import MemberDashboard from "@/pages/member/Dashboard";
import MemberProfile from "@/pages/member/Profile";
import MemberGallery from "@/pages/member/Gallery";
import MemberChat from "@/pages/member/Chat";
import MemberFixtures from "@/pages/member/Fixtures";
import MemberStats from "@/pages/member/Stats";
import MemberEvents from "@/pages/member/Events";
import MemberLoans from "@/pages/member/Loans";
import MemberPlayHQ from "@/pages/member/PlayHQ";
import MemberSponsors from "@/pages/member/Sponsors";

import AdminDashboard from "@/pages/admin/Dashboard";
import AdminMembers from "@/pages/admin/Members";
import AdminMemberDetail from "@/pages/admin/MemberDetail";
import AdminEquipmentLoans from "@/pages/admin/EquipmentLoans";
import AdminGallery from "@/pages/admin/Gallery";
import AdminChats from "@/pages/admin/Chats";
import AdminLanding from "@/pages/admin/Landing";
import AdminEvents from "@/pages/admin/Events";
import AdminNews from "@/pages/admin/News";
import AdminTeams from "@/pages/admin/Teams";
import AdminFixtures from "@/pages/admin/Fixtures";
import AdminPlayHQ from "@/pages/admin/PlayHQ";
import AdminSponsors from "@/pages/admin/Sponsors";
import AdminSponsorshipProposal from "@/pages/admin/SponsorshipProposal";
import AdminSponsorSubmissions from "@/pages/admin/SponsorSubmissions";
import AdminContactSubmissions from "@/pages/admin/ContactSubmissions";
import AdminSystemManagement from "@/pages/admin/SystemManagement";
import AdminGeneralSettings from "@/pages/admin/GeneralSettings";
import AdminRaffles from "@/pages/admin/Raffles";
import AdminPickASportsCard from "@/pages/admin/PickASportsCard";
import AdminShop from "@/pages/admin/Shop";

import Shop from "@/pages/public/Shop";
import ProductDetail from "@/pages/public/ProductDetail";
import ShopCart from "@/pages/public/Cart";
import Checkout from "@/pages/public/Checkout";
import OrderConfirmation from "@/pages/public/OrderConfirmation";

import MemberOrders from "@/pages/member/Orders";

import { CartProvider } from "@/lib/cart";

function RequireMember({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!user || (user.role !== "member" && user.role !== "admin" && user.role !== "primary_admin")) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function RequireAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!user || (user.role !== "admin" && user.role !== "primary_admin")) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

/** Gate for Team Selection — only the primary admin and users with a
 *  coaching role can access. Other admins are bounced back to /a. */
function RequireCoachOrPrimaryAdmin({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
  if (!user || (user.role !== "admin" && user.role !== "primary_admin")) {
    return <Navigate to="/login" replace />;
  }
  const isCoach = Array.isArray(user.coaching_roles) && user.coaching_roles.length > 0;
  if (user.role !== "primary_admin" && !isCoach) {
    return <Navigate to="/a" replace />;
  }
  return children;
}

/* ── Global PWA install-prompt suppression ──
   Intercept the browser's beforeinstallprompt on every page so the
   automatic install mini-infobar never appears (especially on public routes).
   The captured event is stored on window so the member-portal InstallAppCard
   can trigger it from a user click. */
function useGlobalInstallPromptSuppression() {
  React.useEffect(() => {
    function capture(e) {
      e.preventDefault();
      window.__dccDeferredPrompt = e;
      window.dispatchEvent(new Event("dcc:install-prompt-ready"));
    }
    function onInstalled() {
      window.__dccDeferredPrompt = null;
      window.dispatchEvent(new Event("dcc:app-installed"));
    }
    window.addEventListener("beforeinstallprompt", capture);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", capture);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);
}

export default function App() {
  useGlobalInstallPromptSuppression();
  return (
    <AuthProvider>
      <CartProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/news" element={<AllNews />} />
          <Route path="/apply" element={<MemberApply />} />
          <Route path="/sponsor" element={<SponsorProposal />} />
          <Route path="/follow" element={<FollowSignup />} />
          <Route path="/login" element={<MemberLogin />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/admin/login" element={<AdminAuth />} />

          {/* Shop (public) */}
          <Route path="/shop" element={<Shop />} />
          <Route path="/shop/:id" element={<ProductDetail />} />
          <Route path="/shop/cart" element={<ShopCart />} />
          <Route path="/shop/checkout" element={<Checkout />} />
          <Route path="/shop/order/:orderId" element={<OrderConfirmation />} />

          {/* Member portal */}
          <Route path="/m" element={<RequireMember><MemberDashboard /></RequireMember>} />
          <Route path="/m/fixtures" element={<RequireMember><MemberFixtures /></RequireMember>} />
          <Route path="/m/stats" element={<RequireMember><MemberStats /></RequireMember>} />
          <Route path="/m/events" element={<RequireMember><MemberEvents /></RequireMember>} />
          <Route path="/m/profile" element={<RequireMember><MemberProfile /></RequireMember>} />
          <Route path="/m/gallery" element={<RequireMember><MemberGallery /></RequireMember>} />
          <Route path="/m/chat" element={<RequireMember><MemberChat /></RequireMember>} />
          <Route path="/m/chat/:roomId" element={<RequireMember><MemberChat /></RequireMember>} />
          <Route path="/m/loans" element={<RequireMember><MemberLoans /></RequireMember>} />
          <Route path="/m/playhq" element={<RequireMember><MemberPlayHQ /></RequireMember>} />
          <Route path="/m/sponsors" element={<RequireMember><MemberSponsors /></RequireMember>} />
          <Route path="/m/orders" element={<RequireMember><MemberOrders /></RequireMember>} />

          {/* Admin portal */}
          <Route path="/a" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
          <Route path="/a/landing" element={<RequireAdmin><AdminLanding /></RequireAdmin>} />
          <Route path="/a/events" element={<RequireAdmin><AdminEvents /></RequireAdmin>} />
          <Route path="/a/news" element={<RequireAdmin><AdminNews /></RequireAdmin>} />
          <Route path="/a/members" element={<RequireAdmin><AdminMembers /></RequireAdmin>} />
          <Route path="/a/members/:id" element={<RequireAdmin><AdminMemberDetail /></RequireAdmin>} />
          <Route path="/a/equipment-loans" element={<RequireAdmin><AdminEquipmentLoans /></RequireAdmin>} />
          <Route path="/a/teams" element={<RequireCoachOrPrimaryAdmin><AdminTeams /></RequireCoachOrPrimaryAdmin>} />
          <Route path="/a/fixtures" element={<RequireAdmin><AdminFixtures /></RequireAdmin>} />
          <Route path="/a/playhq" element={<RequireAdmin><AdminPlayHQ /></RequireAdmin>} />
          <Route path="/a/gallery" element={<RequireAdmin><AdminGallery /></RequireAdmin>} />
          <Route path="/a/chats" element={<RequireAdmin><AdminChats /></RequireAdmin>} />
          <Route path="/a/sponsors" element={<RequireAdmin><AdminSponsors /></RequireAdmin>} />
          <Route path="/a/sponsors/proposal" element={<RequireAdmin><AdminSponsorshipProposal /></RequireAdmin>} />
          <Route path="/a/sponsors/submissions" element={<RequireAdmin><AdminSponsorSubmissions /></RequireAdmin>} />
          <Route path="/a/contact" element={<RequireAdmin><AdminContactSubmissions /></RequireAdmin>} />
          <Route path="/a/system" element={<RequireAdmin><AdminSystemManagement /></RequireAdmin>} />
          <Route path="/a/system/general" element={<RequireAdmin><AdminGeneralSettings /></RequireAdmin>} />
          <Route path="/a/raffles" element={<RequireAdmin><AdminRaffles /></RequireAdmin>} />
          <Route path="/a/raffles/pick-a-sports-card" element={<RequireAdmin><AdminPickASportsCard /></RequireAdmin>} />
          <Route path="/a/shop" element={<RequireAdmin><AdminShop /></RequireAdmin>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}
